import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Phone, Mail, MapPin } from 'lucide-react';
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaWhatsapp } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import './Footer.css';

const Footer = () => {
    const { t } = useLanguage();

    return (
        <footer className="footer">
            <div className="container">
                <div className="footer-grid">
                    {/* Brand Column */}
                    <div className="footer-brand">
                        <Link to="/" className="footer-logo">
                            <img src="/assets/logo.png" alt="Bharatinsurex" className="footer-logo-img" />
                        </Link>
                        <p>{t('footer_brand_text')}</p>
                        <div className="social-icons">
                            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-fb"><FaFacebookF size={16} /></a>
                            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-tw"><FaTwitter size={16} /></a>
                            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-ig"><FaInstagram size={16} /></a>
                            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-li"><FaLinkedinIn size={16} /></a>
                        </div>
                    </div>

                    {/* Life & Health */}
                    <div className="footer-links">
                        <h4>{t('ft_life')} & {t('ft_health')}</h4>
                        <ul>
                            <li><Link to="/insurance/term-insurance">{t('ft_life_term')}</Link></li>
                            <li><Link to="/insurance/whole-life-insurance">{t('ft_life_whole')}</Link></li>
                            <li><Link to="/insurance/endowment-plan">{t('ft_life_endow')}</Link></li>
                            <li><Link to="/insurance/money-back-plan">{t('ft_life_money')}</Link></li>
                            <li><Link to="/insurance/ulip">{t('ft_life_ulip')}</Link></li>
                            <li><Link to="/insurance/pension-plan">{t('ft_life_pension')}</Link></li>
                            <li><Link to="/insurance/individual-health">{t('ft_health_indiv')}</Link></li>
                            <li><Link to="/insurance/family-floater">{t('ft_health_family')}</Link></li>
                            <li><Link to="/insurance/senior-citizen-health">{t('ft_health_senior')}</Link></li>
                            <li><Link to="/insurance/critical-illness">{t('ft_health_critical')}</Link></li>
                        </ul>
                    </div>

                    {/* Motor & Property */}
                    <div className="footer-links">
                        <h4>{t('ft_motor')} & {t('ft_property')}</h4>
                        <ul>
                            <li><Link to="/insurance/car-insurance">{t('ft_motor_car')}</Link></li>
                            <li><Link to="/insurance/two-wheeler-insurance">{t('ft_motor_bike')}</Link></li>
                            <li><Link to="/insurance/commercial-vehicle">{t('ft_motor_comm')}</Link></li>
                            <li><Link to="/insurance/third-party-motor">{t('ft_motor_third')}</Link></li>
                            <li><Link to="/insurance/comprehensive-motor">{t('ft_motor_compre')}</Link></li>
                            <li><Link to="/insurance/home-insurance">{t('ft_prop_home')}</Link></li>
                            <li><Link to="/insurance/fire-insurance">{t('ft_prop_fire')}</Link></li>
                            <li><Link to="/insurance/burglary-insurance">{t('ft_prop_burg')}</Link></li>
                            <li><Link to="/insurance/shop-insurance">{t('ft_prop_shop')}</Link></li>
                            <li><Link to="/insurance/industrial-insurance">{t('ft_prop_indus')}</Link></li>
                        </ul>
                    </div>

                    {/* Business & Travel */}
                    <div className="footer-links">
                        <h4>{t('ft_business')} & {t('ft_travel')}</h4>
                        <ul>
                            <li><Link to="/insurance/liability-insurance">{t('ft_biz_liab')}</Link></li>
                            <li><Link to="/insurance/professional-indemnity">{t('ft_biz_indem')}</Link></li>
                            <li><Link to="/insurance/cyber-insurance">{t('ft_biz_cyber')}</Link></li>
                            <li><Link to="/insurance/marine-insurance">{t('ft_biz_marine')}</Link></li>
                            <li><Link to="/insurance/workmen-compensation">{t('ft_biz_comp')}</Link></li>
                            <li><Link to="/insurance/domestic-travel">{t('ft_travel_dom')}</Link></li>
                            <li><Link to="/insurance/international-travel">{t('ft_travel_intl')}</Link></li>
                            <li><Link to="/insurance/student-travel">{t('ft_travel_stud')}</Link></li>
                            <li><Link to="/insurance/family-travel">{t('ft_travel_fam')}</Link></li>
                        </ul>
                    </div>

                    {/* Others & Company */}
                    <div className="footer-links">
                        <h4>{t('footer_company')} & {t('ft_others')}</h4>
                        <ul>
                            <li><Link to="/about">{t('footer_about')}</Link></li>
                            <li><Link to="/insurance/mobile-insurance">{t('ft_oth_mobile')}</Link></li>
                            <li><Link to="/insurance/pet-insurance">{t('ft_oth_pet')}</Link></li>
                            <li><Link to="/insurance/event-insurance">{t('ft_oth_event')}</Link></li>
                            <li><Link to="/insurance/wedding-insurance">{t('ft_oth_wed')}</Link></li>
                        </ul>
                    </div>

                    {/* Dedicated Contact Column */}
                    <div className="footer-links contact-column">
                        <h4>{t('footer_contact')}</h4>
                        <ul className="contact-list">
                            <li>
                                <a href="tel:+919667193590" className="contact-item tel">
                                    <span className="contact-icon icon-tel"><Phone size={18} /></span>
                                    <div className="contact-text">
                                        <small>{t('contact_call_title')}</small>
                                        <span>+91 96671 93590</span>
                                    </div>
                                </a>
                            </li>
                            <li>
                                <a href="https://wa.me/919667193590" target="_blank" rel="noopener noreferrer" className="contact-item whatsapp">
                                    <span className="contact-icon icon-wa"><FaWhatsapp size={18} /></span>
                                    <div className="contact-text">
                                        <small>WhatsApp</small>
                                        <span>+91 96671 93590</span>
                                    </div>
                                </a>
                            </li>
                            <li>
                                <a href="mailto:support@bharatinsurex.com" className="contact-item email">
                                    <span className="contact-icon icon-email"><Mail size={18} /></span>
                                    <div className="contact-text">
                                        <small>{t('contact_email_title')}</small>
                                        <span>support@bharatinsurex.com</span>
                                    </div>
                                </a>
                            </li>
                            <li>
                                <a href="https://maps.google.com/?q=New+Delhi" target="_blank" rel="noopener noreferrer" className="contact-item branch">
                                    <span className="contact-icon icon-branch"><MapPin size={18} /></span>
                                    <div className="contact-text">
                                        <small>{t('contact_visit_title')}</small>
                                        <span>{t('nav_branch')}</span>
                                    </div>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="footer-bottom">
                    <p>{t('footer_copyright').replace('{year}', new Date().getFullYear())}</p>
                    <div className="footer-bottom-links">
                        <Link to="/privacy">Privacy Policy</Link>
                        <Link to="/terms">Terms of Service</Link>
                        <Link to="/contact">Support Center</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
